import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

/**
 * Создание учётной записи в Active Directory.
 *
 * Принимает «предзаполнение» из формы сотрудника (ФИО, email) — на их основе
 * сразу составляются Фамилия / Имя / Отчество. Поля «sAMAccountName» и пароль
 * пользователь вводит сам.
 *
 * onCreated({ upn, dn, displayName, sam })  — после успеха
 */
export default function AdUserCreateModal({ initial, onClose, onCreated }) {
  const split = splitFio(initial?.name || '');

  const [surname,   setSurname]   = useState(split.surname);
  const [givenName, setGivenName] = useState(split.given);
  const [midName,   setMidName]   = useState(split.mid);
  const [sam,       setSam]       = useState(suggestSam(split.surname, split.given, split.mid));
  const [samManual, setSamManual] = useState(false);
  const [email,     setEmail]     = useState(initial?.email || '');
  const [addToGroups, setAddToGroups] = useState(true);

  // Пока пользователь не правил sAMAccountName руками — пересчитываем его
  // автоматически при правке Фамилии/Имени/Отчества.
  useEffect(() => {
    if (samManual) return;
    setSam(suggestSam(surname, givenName, midName));
  }, [surname, givenName, midName, samManual]);

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [groupResults, setGroupResults] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!surname.trim() || !givenName.trim()) { setError('Укажите фамилию и имя'); return; }
    if (!sam.trim() || sam.length > 20)        { setError('sAMAccountName обязателен (до 20 символов)'); return; }

    setSaving(true);
    try {
      const res = await api.post('/api/ad/users', {
        sam: sam.trim(),
        surname: surname.trim(),
        givenName: givenName.trim(),
        midName: midName.trim(),
        email: email.trim() || null,
        addToDefaultGroups: addToGroups,
      });
      // Если есть группы с ошибкой — покажем под формой, но всё равно отдадим
      // событие на родителя (учётка создана и активирована).
      const failed = (res.groups || []).filter((g) => !g.ok);
      if (failed.length) setGroupResults(res.groups);
      onCreated?.({
        upn: res.upn,
        dn: res.dn,
        sam: res.sam,
        displayName: res.displayName,
      });
      if (!failed.length) onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось создать'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" style={{ zIndex: 110 }} onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true" style={{ maxWidth: 520 }}>
        <div className="sd-edit__head">
          <h2>Создать пользователя в Active Directory</h2>
          <button className="sd-close" onClick={onClose} disabled={saving}><Icon name="x" size={14} /></button>
        </div>
        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field">
              <label className="sd-edit__label">Фамилия *</label>
              <input className="sd-edit__input" value={surname} onChange={(e) => setSurname(e.target.value)} required autoFocus />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Имя *</label>
              <input className="sd-edit__input" value={givenName} onChange={(e) => setGivenName(e.target.value)} required />
            </div>
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Отчество</label>
              <input className="sd-edit__input" value={midName} onChange={(e) => setMidName(e.target.value)} />
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">sAMAccountName *</label>
              <input
                className="sd-edit__input mono"
                value={sam}
                onChange={(e) => { setSam(e.target.value.replace(/\s+/g, '')); setSamManual(true); }}
                maxLength={20}
                placeholder="IvanovIP"
                required
              />
              <div className="sd-edit__hint">
                По умолчанию — паспортная транслитерация: «ФамилияИО» (например, Иванов Пётр Семёнович → IvanovPS).
                Можно изменить вручную; до 20 символов, без пробелов.
              </div>
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Email (опц.)</label>
              <input className="sd-edit__input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <div className="staff-info-block">
                <Icon name="info" size={13} />
                <div>
                  Будет использован <b>пароль по умолчанию</b> из настроек AD. Сотрудник
                  обязан сменить его при первом входе.
                </div>
              </div>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="staff-auth-row">
                <input type="checkbox" checked={addToGroups} onChange={(e) => setAddToGroups(e.target.checked)} />
                <span>Добавить в группы по умолчанию (из настроек AD)</span>
              </label>
            </div>

            {groupResults && (
              <div className="sd-edit__field sd-edit__field--full">
                <div className="sd-edit__hint" style={{ color: 'var(--red-fg)' }}>
                  Учётка создана и активирована, но не во все группы получилось добавить:
                </div>
                <ul style={{ margin: '4px 0 0 18px', fontSize: 12 }}>
                  {groupResults.filter((g) => !g.ok).map((g) => (
                    <li key={g.dn}><span className="mono">{g.dn}</span> — {g.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Создаём…' : 'Создать в AD'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// «Сухановский Я.А.» / «Иванов Иван Иванович» → {surname, given, mid}
function splitFio(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { surname: '', given: '', mid: '' };
  if (parts.length === 1) return { surname: parts[0], given: '', mid: '' };
  return { surname: parts[0], given: parts[1] || '', mid: parts.slice(2).join(' ') };
}

// Иванов / Пётр / Семёнович → «IvanovPS». Транслитерация — паспортная
// (ICAO Doc 9303 / приказ МВД №864): я→ia, ю→iu, й→i, ц→ts, ч→ch, ш→sh,
// щ→shch, х→kh, ж→zh, ё→e, ъ→ie, ь→(пусто).
function suggestSam(surname, given, mid) {
  const sur = capitalize(translit((surname || '').replace(/[^а-яёa-z]/giu, '')));
  const giv = capInitial(translit((given || '').charAt(0)));
  const m   = capInitial(translit((mid   || '').charAt(0)));
  return (sur + giv + m).slice(0, 20);
}

// Транслитерация: каждая русская буква → латинский эквивалент. Латинские буквы
// и пр. символы пропускаем как есть.
const PASSPORT_TRANSLIT = {
  а:'a', б:'b', в:'v', г:'g', д:'d', е:'e', ё:'e', ж:'zh', з:'z', и:'i', й:'i',
  к:'k', л:'l', м:'m', н:'n', о:'o', п:'p', р:'r', с:'s', т:'t', у:'u', ф:'f',
  х:'kh', ц:'ts', ч:'ch', ш:'sh', щ:'shch', ъ:'ie', ы:'y', ь:'', э:'e', ю:'iu', я:'ia',
};

function translit(s) {
  return [...(s || '')].map((c) => {
    const lower = c.toLowerCase();
    return PASSPORT_TRANSLIT[lower] !== undefined ? PASSPORT_TRANSLIT[lower] : lower;
  }).join('');
}

function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Берёт ПЕРВЫЙ символ переданной строки и делает заглавным. Нужно потому что
// одна русская буква может перевестись в несколько латинских (Ю → iu, Я → ia),
// а для инициала нужна только одна.
function capInitial(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase();
}
